import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { useMachine } from '@xstate/react';
import { c } from 'ttag';

import { Button } from '@proton/atoms/Button/Button';
import type { ProductParam } from '@proton/shared/lib/apps/product';
import type { APP_NAMES } from '@proton/shared/lib/constants';

import type { OnLoginCallback } from '../content/authSession';
import Layout from '../public/Layout';
import Main from '../public/Main';
import { useResetPasswordTelemetry } from '../reset/resetPasswordTelemetry';
import { type MetaTags, useMetaTags } from '../useMetaTags';
import { UnauthedForgotPasswordWizard } from './UnauthedForgotPasswordWizard';
import {
    UnauthedForgotPasswordStateMachine,
    UnauthedForgotPasswordStateMachineTags,
} from './state-machine/UnauthedForgotPasswordStateMachine';

interface Props {
    onLogin: OnLoginCallback;
    toApp: APP_NAMES | undefined;
    setupVPN: boolean;
    loginUrl: string;
    productParam: ProductParam;
    onPreSubmit: () => Promise<void>;
    onStartAuth: () => Promise<void>;
    metaTags: MetaTags;
}

export const ResetPasswordPage = ({
    toApp,
    loginUrl,
    onPreSubmit,
    onStartAuth,
    onLogin,
    setupVPN,
    productParam,
    metaTags,
}: Props) => {
    useMetaTags(metaTags);
    const history = useHistory();
    const redirectToSignIn = () => history.push(loginUrl);
    const { sendResetPasswordPageLoad, sendResetPasswordPageExit } = useResetPasswordTelemetry({ variant: 'B' });

    useEffect(() => {
        sendResetPasswordPageLoad();
        return () => {
            sendResetPasswordPageExit();
        };
    }, []);

    const [snapshot, send, actorRef] = useMachine(
        UnauthedForgotPasswordStateMachine.provide({
            actions: {
                redirectToSignIn,
            },
        })
    );

    const handleBackStep = () => send({ type: 'decision.back' });

    return (
        <Layout toApp={toApp} hasDecoration={snapshot.value === 'entry'} onBack={handleBackStep}>
            <Main>
                <UnauthedForgotPasswordWizard
                    actorRef={actorRef}
                    snapshot={snapshot}
                    send={send}
                    onPreSubmit={onPreSubmit}
                    onStartAuth={onStartAuth}
                    onLogin={onLogin}
                    setupVPN={setupVPN}
                    productParam={productParam}
                />
            </Main>
            {!snapshot.hasTag(UnauthedForgotPasswordStateMachineTags.hideReturnToSignIn) && (
                <div className="text-center">
                    <Button size="large" color="norm" shape="ghost" className="mt-2" onClick={redirectToSignIn}>{c(
                        'Action'
                    ).t`Return to sign-in`}</Button>
                </div>
            )}
        </Layout>
    );
};
